import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Music2, MailWarning } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Page publique d'acceptation d'invitation (§26-27).
// - nouvel utilisateur : création du compte (mot de passe choisi par l'invité) puis membership
// - utilisateur existant non connecté : renvoi vers la connexion
// - connecté avec le mauvais compte : avertissement clair, jamais de rattachement automatique
export default function InvitationPage() {
  const { token } = useParams();
  const { user, identity, setAuth, logout, t, lang } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [wrongAccount, setWrongAccount] = useState("");
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invitations/accept/${token}`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview({ status: "invalid" }));
  }, [token]);

  const loggedIn = !!user || !!identity;

  const doAccept = async (e) => {
    e?.preventDefault();
    setError("");
    setWrongAccount("");
    if (!loggedIn && !(preview?.existing_account || requiresLogin)) {
      if (password.length < 8) { setError(t("auth.pwd_rule")); return; }
      if (password !== confirm) { setError(t("param.pwd_mismatch")); return; }
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/invitations/accept/${token}`, { password, lang });
      if (data.requires_login) {
        setRequiresLogin(true);
        return;
      }
      setAuth(data);
      toast.success(t("inv.welcome", { name: data.group?.name || "" }));
      navigate("/tableau-de-bord");
    } catch (err) {
      if (err.response?.status === 409) setWrongAccount(err.friendly);
      else setError(err.friendly);
    } finally {
      setLoading(false);
    }
  };

  const switchAccount = async () => {
    await logout();
    navigate(`/connexion?next=${encodeURIComponent(`/invitation/${token}`)}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <p className="font-heading text-3xl font-bold brand">RANNEA</p>
          <p className="t-secondary text-sm mt-1">The link between musicians</p>
        </div>
        <div className="app-card p-6 space-y-4 anim-fade-up" data-testid="invitation-page">
          {!preview && <p className="text-sm t-secondary text-center" data-testid="invitation-loading">{t("inv.loading")}</p>}

          {preview && preview.status !== "valid" && (
            <div className="text-center space-y-3" data-testid={`invitation-${preview.status}`}>
              <p className="text-sm">
                {preview.status === "invalid" && t("inv.invalid")}
                {preview.status === "expired" && t("inv.expired")}
                {preview.status === "cancelled" && t("inv.cancelled")}
                {preview.status === "accepted" && t("inv.accepted")}
              </p>
              <Link to="/connexion" className="text-sm brand hover:underline inline-block" data-testid="invitation-back-login">
                {t("inv.back_login")}
              </Link>
            </div>
          )}

          {preview && preview.status === "valid" && (
            <>
              <div className="text-center">
                <h1 className="font-heading font-bold text-lg">{t("inv.title")}</h1>
                <p className="text-sm t-secondary mt-1">
                  {t("inv.for")} <span className="font-mono">{preview.email}</span>
                </p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg border border-app bg-white/5" data-testid="invitation-group-card">
                <div className="h-10 w-10 rounded-lg brand-bg flex items-center justify-center shrink-0">
                  <Music2 className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{preview.group_name}</p>
                  {preview.invited_by && <p className="text-xs t-secondary truncate">{t("inv.invited_by", { name: preview.invited_by })}</p>}
                </div>
              </div>

              {error && <p className="text-sm text-rose-400" data-testid="invitation-error">{error}</p>}

              {wrongAccount ? (
                <div className="space-y-3" data-testid="invitation-wrong-account">
                  <div className="flex gap-2 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10">
                    <MailWarning className="h-5 w-5 text-amber-400 shrink-0" />
                    <p className="text-sm">{wrongAccount}</p>
                  </div>
                  <Button className="w-full brand-bg text-white" onClick={switchAccount} data-testid="invitation-switch-account">
                    {t("inv.wrong_logout")}
                  </Button>
                </div>
              ) : loggedIn ? (
                <>
                  <p className="text-xs t-secondary text-center" data-testid="invitation-logged-as">
                    {t("inv.logged_as", { email: user?.login_id || identity?.email })}
                  </p>
                  <Button className="w-full brand-bg text-white hover:opacity-90" onClick={() => doAccept()} disabled={loading} data-testid="invitation-accept-button">
                    {loading ? t("connecting") : t("inv.join_button")}
                  </Button>
                </>
              ) : (preview.existing_account || requiresLogin) ? (
                <div className="space-y-3" data-testid="invitation-requires-login">
                  <p className="text-sm t-secondary">{t("inv.existing_account")}</p>
                  <Button className="w-full brand-bg text-white" onClick={() => navigate(`/connexion?next=${encodeURIComponent(`/invitation/${token}`)}`)} data-testid="invitation-login-button">
                    {t("inv.login_button")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={doAccept} className="space-y-3" data-testid="invitation-create-form">
                  <p className="text-sm t-secondary">{t("inv.create_account")}</p>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("password_label")}</Label>
                    <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                      className="mt-1 bg-transparent border-app" data-testid="invitation-password-input" />
                  </div>
                  <div>
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t("confirmation")}</Label>
                    <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
                      className="mt-1 bg-transparent border-app" data-testid="invitation-confirm-input" />
                  </div>
                  <p className="text-xs t-secondary">{t("auth.pwd_rule")}</p>
                  <Button type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90" data-testid="invitation-create-submit">
                    {loading ? t("connecting") : t("inv.create_and_join")}
                  </Button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
